// Resguardo local reforzado (Tanda 3 — Blindaje de datos): una copia de TODO
// lo que hay en este dispositivo (Dexie/IndexedDB), exportada como archivo
// JSON guardado en el propio dispositivo. Funciona 100% offline — no
// depende del servidor ni de señal, es un resguardo adicional al backup en
// la nube (GridFS) por si el dispositivo se pierde antes de sincronizar.
import { db, getMeta, setMeta, META, nowIso } from '@/lib/db';

const LOCAL_BACKUP_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000; // semanal

async function buildLocalBackupPayload() {
  const [farms, lots, animals, events, inventories, inventory_scans] = await Promise.all([
    db.farms.toArray(), db.lots.toArray(), db.animals.toArray(),
    db.events.toArray(), db.inventories.toArray(), db.inventory_scans.toArray(),
  ]);
  return {
    app: 'Cuenta Vaca',
    backup_version: 1,
    source: 'local_device',
    generated_at: nowIso(),
    counts: {
      farms: farms.length, lots: lots.length, animals: animals.length,
      events: events.length, inventories: inventories.length, inventory_scans: inventory_scans.length,
    },
    data: { farms, lots, animals, events, inventories, inventory_scans },
  };
}

function triggerJsonDownload(payload, filenamePrefix) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export async function downloadLocalBackupNow() {
  const payload = await buildLocalBackupPayload();
  triggerJsonDownload(payload, 'cuenta-vaca-resguardo-local');
  await setMeta(META.LAST_LOCAL_EXPORT_AT, nowIso());
  return payload.counts;
}

// Fix I (2026-09-17): copia descargable de lo que NO llegó al servidor, para
// ofrecerla ANTES del escape destructivo ("salir igual y perder los datos").
// El archivo usa el MISMO formato que acepta POST /api/migration/import, así
// que sirve para RECUPERAR, no solo para mirar:
//   Ajustes → Exportar/Migración → Importar archivo, con modo "merge"
//   (agrega/actualiza por id, sin borrar lo que ya existe en el servidor).
// Además incluye la cola cruda (sync_queue / actions_outbox) para diagnóstico
// de soporte; el importador ignora esa sección.
function stripLocalFields(rec) {
  const { _sync_state, _local_updated_at, ...rest } = rec;
  return rest;
}

export async function buildPendingBackupPayload() {
  const [queue, outbox] = await Promise.all([
    db.sync_queue.where('status').notEqual('done').toArray(),
    db.actions_outbox.where('status').notEqual('done').toArray(),
  ]);
  // Ids referenciados por la cola + todo lo que localmente no está confirmado.
  const queuedIds = new Set(queue.map((q) => q.entity_id).filter(Boolean));
  const entities = [
    ['farms', db.farms], ['lots', db.lots], ['animals', db.animals],
    ['events', db.events], ['inventories', db.inventories], ['inventory_scans', db.inventory_scans],
  ];
  const data = {};
  const counts = {};
  for (const [name, table] of entities) {
    const rows = await table.filter((r) => r._sync_state !== 'synced' || queuedIds.has(r.id)).toArray();
    data[name] = rows.map(stripLocalFields);
    counts[name] = rows.length;
  }
  const currentUser = await getMeta(META.CURRENT_USER);
  const ownerScope = await getMeta(META.LAST_OWNER_SCOPE);
  return {
    app: 'Cuenta Vaca',
    format_version: 1,
    source: 'pending_queue_backup',
    exported_at: nowIso(),
    generated_at: nowIso(),
    source_owner_id: ownerScope || (currentUser?.role === 'owner' ? currentUser?.id : currentUser?.owner_id) || null,
    source_owner_email: currentUser?.email || currentUser?.username || null,
    counts: { ...counts, sync_queue: queue.length, actions_outbox: outbox.length },
    data,
    pending_queue: { sync_queue: queue, actions_outbox: outbox },
    recovery_instructions:
      'Para recuperar estos registros: entrá con la cuenta del dueño y usá la importación de datos ' +
      '(POST /api/migration/import) con este archivo y modo "merge". Ese modo agrega/actualiza por id ' +
      'y no borra nada de lo que ya esté en el servidor. La sección pending_queue es solo para soporte.',
  };
}

export async function downloadPendingBackup() {
  const payload = await buildPendingBackupPayload();
  triggerJsonDownload(payload, 'cuenta-vaca-pendientes-sin-subir');
  return payload.counts;
}

export async function getLastLocalExportAt() {
  return getMeta(META.LAST_LOCAL_EXPORT_AT);
}

// Se llama una vez al abrir la app (con usuario logueado). Si pasó una
// semana (o nunca se hizo), genera y descarga la copia sola — sin pedirle
// nada al usuario — y avisa con un toast. No requiere señal: es 100% local.
export async function maybeAutoLocalBackup() {
  const last = await getMeta(META.LAST_LOCAL_EXPORT_AT);
  const due = !last || (Date.now() - new Date(last).getTime() > LOCAL_BACKUP_INTERVAL_MS);
  if (!due) return null;
  const hasData = (await db.animals.count()) > 0 || (await db.farms.count()) > 0;
  if (!hasData) return null; // nada que resguardar todavía
  try {
    return await downloadLocalBackupNow();
  } catch (e) {
    return null;
  }
}
